import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import TournamentCard from "@/components/TournamentCard";
import { useWTAHistoryGlobal } from "@/lib/hooks/useApi";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { t } from "@/lib/i18n";

type RoomFilter = {
  key: "ALL" | "PRO" | "SEMI_PRO" | "AMATEUR";
  label: string;
  entryUsdc?: number;
};

type RoomFilterKey = RoomFilter["key"];

function WinnerTakesAllHistory() {
  const ROOM_FILTERS: RoomFilter[] = useMemo(
    () => [
      { key: "ALL", label: t("winnerTakesAllHistory.all") },
      { key: "PRO", label: t("winnerTakesAllHistory.proDivision"), entryUsdc: 0.1 },
      { key: "SEMI_PRO", label: t("winnerTakesAllHistory.semiProDivision"), entryUsdc: 0.05 },
      { key: "AMATEUR", label: t("winnerTakesAllHistory.amateurDivision"), entryUsdc: 0.01 },
    ],
    [],
  );
  const router = useRouter();
  const [selectedDivision, setSelectedDivision] =
    useState<RoomFilterKey>("ALL");
  const [page, setPage] = useState(1);
  const [allTournaments, setAllTournaments] = useState<
    NonNullable<ReturnType<typeof useWTAHistoryGlobal>["data"]>
  >([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading, isFetching } = useWTAHistoryGlobal(page);

  // Accumulate tournament data as pages load
  useEffect(() => {
    if (data && data.length > 0) {
      setAllTournaments((prev) => {
        const existingIds = new Set(prev.map((t) => t.tournamentId));
        const newTournaments = data.filter(
          (t) => !existingIds.has(t.tournamentId),
        );
        return [...prev, ...newTournaments];
      });

      if (data.length < 30) {
        setHasMore(false);
      }
    } else if (data && data.length === 0 && page > 1) {
      setHasMore(false);
    }
  }, [data, page]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching, hasMore],
  );

  const filteredHistory = useMemo(() => {
    if (selectedDivision === "ALL") {
      return allTournaments;
    }

    const division = ROOM_FILTERS.find(
      (filter) => filter.key === selectedDivision,
    );

    if (!division?.entryUsdc) {
      return allTournaments;
    }

    return allTournaments.filter(
      (tournament) => Number(tournament.betAmount) === division.entryUsdc,
    );
  }, [allTournaments, selectedDivision, ROOM_FILTERS]);

  const selectedDivisionLabel =
    ROOM_FILTERS.find((filter) => filter.key === selectedDivision)?.label ??
    "Selected";

  // Reset scroll position when filter changes
  useEffect(() => {
    const viewport = document.querySelector(
      '[data-slot="scroll-area-viewport"]',
    ) as HTMLElement;
    if (viewport) {
      viewport.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedDivision]);

  if (isLoading && page === 1) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-[#1D2A52] flex flex-1 flex-col h-full overflow-hidden p-1 gap-4">
      <FloatingLeftButton
        onClick={() => router.navigate({ to: "/winner-takes-all" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="flex-1 flex flex-col p-1 gap-4 w-full min-h-0 mt-10">
        <div className="text-center flex flex-col gap-2">
          <p className="text-white text-3xl font-extrabold text-shadow-black">
            {t("winnerTakesAllHistory.winnerTakesAllHistory")}
          </p>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* <div className="flex flex-col gap-2">
            <div className="text-white/70 text-xs uppercase tracking-wide">
              {t("winnerTakesAllHistory.filterByDivision")}
            </div>
            <Select
              value={selectedDivision}
              onValueChange={(value) =>
                setSelectedDivision(value as RoomFilterKey)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("winnerTakesAllHistory.selectDivision")} />
              </SelectTrigger>
              <SelectContent>
                {ROOM_FILTERS.map((filter) => (
                  <SelectItem key={filter.key} value={filter.key}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div> */}

          <ScrollArea className="flex-1 min-h-0 mt-3" type="scroll">
            <div className="space-y-3">
              {filteredHistory.length ? (
                <>
                  {filteredHistory.map((tournament, index) => (
                    <div
                      key={`${tournament.tournamentId}-${index}`}
                      className="w-full"
                      ref={
                        index === filteredHistory.length - 1
                          ? lastElementRef
                          : undefined
                      }
                    >
                      <TournamentCard
                        tournament={tournament}
                        asLink
                        isWinnerTakesAll
                        hideUserWinnings
                        isHistoryGlobal
                      />
                    </div>
                  ))}
                  {isFetching && hasMore && page > 1 && (
                    <div className="flex items-center justify-center py-4">
                      <Spinner />
                    </div>
                  )}
                  {!hasMore && filteredHistory.length > 0 && (
                    <div className="flex items-center justify-center py-4 text-white/50 text-sm">
                      {t("winnerTakesAllHistory.youveReachedTheEnd")}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-white/70">
                  <p className="text-lg font-semibold">
                    {t("winnerTakesAllHistory.noTournamentsFound", { selectedDivisionLabel })}
                  </p>
                  <p className="text-sm">
                    {t("winnerTakesAllHistory.trySelectingAnotherDivision")}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/(game)/winner-takes-all/history")({
  component: WinnerTakesAllHistory,
});
